import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Footer from '../components/Footer';
import Header from '../components/Header';
import PanoramaCard from '../components/PanoramaCard';
import { panoramaProjects } from '../Data/mainPageProjects';
import Logo from '../assets/V4LOGOWhite.png';
import 'ol/ol.css';
import { Map, View } from 'ol';
import { Tile as TileLayer, Vector as VectorLayer } from "ol/layer";
import { fromLonLat } from 'ol/proj';
import { TileWMS, Cluster, Vector as VectorSource } from "ol/source";

import { Feature } from "ol";
import { Style, Text, Fill, Stroke, Circle as CircleStyle } from "ol/style";
import { Point } from "ol/geom";

import background from '../assets/background.png';

const SOUTH_KOREA_EXTENT = [
  ...fromLonLat([124.2, 32.7]),
  ...fromLonLat([132.1, 38.9]),
];

const MainPage = () => {
  const mapRef = useRef(null);
  const [features, setFeatures] = useState([]);

  useEffect(() => {
    fetch("http://v4.dromii.com/api/pano/panoramas/list/")
    .then((res) => res.json())
    .then((data) => {
      if (!Array.isArray(data)) {
        throw new Error("Invalid API response: Expected an array");
      }
  
      const newFeatures = data.map((item) => new Feature({
        geometry: new Point(fromLonLat([item.longitude, item.latitude])),
        id: item.id,
        title: item.title,
        scene_name: item.scene_name,
      }));
  
      setFeatures(newFeatures);
    })
    .catch((err) => console.error("Error fetching data:", err));
  
  }, []);

  useEffect(() => {
    if (!mapRef.current || features.length === 0) return;

    const clusterSource = new Cluster({
      distance: 50,
      source: new VectorSource({ features }),
    });

    const clusterStyle = (feature) => {
      const size = feature.get("features").length;
      return new Style({
        image: new CircleStyle({
          radius: size > 1 ? 15 : 10,
          fill: new Fill({ color: size > 1 ? "rgba(255, 165, 0, 0.8)" : "rgba(0, 153, 255, 0.8)" }),
          stroke: new Stroke({ color: "#fff", width: 2 }),
        }),
        text: new Text({
          text: size > 1 ? size.toString() : "",
          fill: new Fill({ color: "#fff" }),
          stroke: new Stroke({ color: "#000", width: 3 }),
          font: "bold 12px sans-serif",
        }),
      });
    };

    const clusterLayer = new VectorLayer({
      source: clusterSource,
      style: clusterStyle,
    });

    const KoreaAOI = new TileLayer({
      source: new TileWMS({
        url: "https://k-aquas.dromii.com/geoserver/yeongju/wms",
        params: {
          LAYERS: "yeongju:koreaAOI",
          TILED: true,
        },
        serverType: "geoserver",
        transition: 0,
      }),
    });

    const mapInstance = new Map({
      target: mapRef.current,
      layers: [
       
        KoreaAOI, 
        clusterLayer,
      ],
      view: new View({
        center: fromLonLat([127.9538, 36.3857]), // 한국 중심
        zoom: 7.5,
        minZoom: 7.5,
        maxZoom: 10.5,
        extent: SOUTH_KOREA_EXTENT,
        constrainOnlyCenter: false,
        smoothExtentConstraint: false,
        enableRotation: false,
      }),
    });

    return () => {
      mapInstance.setTarget(null);
    };
  }, [features]);

  return (
   <Page>
      <Header />
      <main>
        <Hero>
          <HeroBackground src={background} alt="" />
          <HeroOverlay />
          <HeroContent>
            <HeroLogo src={Logo} alt="K360°REA" />
            <Eyebrow>360° SPATIAL EXPERIENCE</Eyebrow>
            <HeroTitle>공간을 기록하고,<br />현장을 더<MobileBreak />가깝게 연결합니다.</HeroTitle>
            <HeroDescription>드로미의 파노라마 서비스로 대한민국 곳곳의 풍경과 현장을 생생하게 경험해 보세요.</HeroDescription>
            <HeroLink href="#services">서비스 둘러보기 <span>↓</span></HeroLink>
          </HeroContent>
        </Hero>

        <Container>
          <Section id="services">
            <SectionHeader>
              <SectionCopy>
                <Eyebrow $dark>PANORAMA PORTFOLIO</Eyebrow>
                <SectionTitle>드로미 파노라마 프로젝트</SectionTitle>
              </SectionCopy>
              <SectionDescription>드로미가 제작한 주요 파노라마 콘텐츠입니다.</SectionDescription>
            </SectionHeader>
            <ProjectGrid>
              {panoramaProjects.map((project) => <PanoramaCard key={project.title} {...project} />)}
            </ProjectGrid>
          </Section>

          <Section $mapSection>
            <SectionHeader>
              <SectionCopy>
                <Eyebrow $dark>NATIONWIDE COVERAGE</Eyebrow>
                <SectionTitle>전국 Pano 촬영지역</SectionTitle>
              </SectionCopy>
              <SectionDescription>현재 기록된 파노라마 촬영 지점을 지도에서 한눈에 확인할 수 있습니다.</SectionDescription>
            </SectionHeader>
            <MapFrame>
              <MapContainer ref={mapRef} aria-label="전국 파노라마 촬영지역 지도" />
            </MapFrame>
          </Section>
        </Container>
      </main>
      <Footer />
      </Page>
  );
};

export default MainPage;

const Page = styled.div`
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  background: #f6f8f7;
`;

const Hero = styled.section`
  position: relative;
  display: grid;
  min-width: 0;
  min-height: min(700px, calc(100vh - 76px));
  place-items: center;
  overflow: hidden;
  background: #173741;
`;

const HeroBackground = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, rgba(10, 27, 33, 0.78), rgba(10, 27, 33, 0.42) 52%, rgba(10, 27, 33, 0.24));
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  width: min(1120px, calc(100% - 48px));
  min-width: 0;
  padding: 72px 0;
  color: #ffffff;
`;

const HeroLogo = styled.img`
  display: block;
  width: clamp(145px, 15vw, 220px);
  margin-bottom: clamp(42px, 8vw, 80px);
`;

const Eyebrow = styled.p`
  margin: 0 0 14px;
  color: ${({ $dark }) => ($dark ? '#478ba6' : '#b8e5eb')};
  font-size: clamp(0.76rem, 0.82vw, 0.9rem);
  font-weight: 900;
  letter-spacing: 0.15em;
  line-height: 1.2;
`;

const HeroTitle = styled.h1`
  max-width: 780px;
  min-width: 0;
  margin: 0;
  font-size: clamp(2.7rem, 5.2vw, 4.8rem);
  font-weight: var(--font-weight-title);
  letter-spacing: -0.075em;
  line-height: 1.02;
  word-break: keep-all;

  @media (max-width: 560px) {
    max-width: 100%;
    font-size: 2.15rem;
    letter-spacing: -0.07em;
    line-height: 1.12;
    word-break: break-all;
  }
`;

const MobileBreak = styled.br`
  display: none;

  @media (max-width: 560px) {
    display: block;
  }
`;

const HeroDescription = styled.p`
  max-width: none;
  margin: 26px 0 0;
  color: rgba(255, 255, 255, 0.8);
  font-size: clamp(1rem, 1.12vw, 1.18rem);
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1.72;
  white-space: nowrap;
  word-break: keep-all;

  @media (max-width: 560px) {
    white-space: normal;
    word-break: break-all;
  }
`;

const HeroLink = styled.a`
  display: inline-flex;
  gap: 28px;
  align-items: center;
  margin-top: 38px;
  padding: 15px 18px 15px 22px;
  border: 1px solid rgba(255, 255, 255, 0.42);
  border-radius: 999px;
  color: #ffffff;
  font-size: 0.92rem;
  font-weight: 850;
  text-decoration: none;
  transition: background 180ms ease, color 180ms ease;

  &:hover,
  &:focus-visible {
    background: #ffffff;
    color: #173741;
  }

  span {
    font-size: 1.2rem;
  }
`;

const Container = styled.div`
  width: min(1120px, calc(100% - 48px));
  margin: 0 auto;
`;

const Section = styled.section`
  padding: ${({ $mapSection }) => ($mapSection ? '72px 0 116px' : '112px 0 0')};

  @media (max-width: 680px) {
    padding: ${({ $mapSection }) => ($mapSection ? '64px 0 76px' : '76px 0 0')};
  }
`;

const SectionHeader = styled.div`
  display: flex;
  gap: 32px;
  align-items: end;
  justify-content: space-between;
  margin-bottom: 34px;

  @media (max-width: 720px) {
    align-items: start;
    flex-direction: column;
    gap: 16px;
  }
`;

const SectionCopy = styled.div`
  flex: 0 0 auto;
`;

const SectionTitle = styled.h2`
  margin: 0;
  color: #1d3038;
  font-size: clamp(2.2rem, 3.65vw, 3.25rem);
  font-weight: var(--font-weight-title);
  letter-spacing: -0.07em;
  line-height: 1.04;
  word-break: keep-all;
`;

const SectionDescription = styled.p`
  max-width: 590px;
  margin: 0 0 4px;
  color: #66767d;
  font-size: clamp(0.98rem, 1.02vw, 1.08rem);
  font-weight: 600;
  letter-spacing: -0.025em;
  line-height: 1.7;
  white-space: nowrap;
  word-break: keep-all;

  @media (max-width: 720px) {
    white-space: normal;
  }
`;

const ProjectGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 24px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
  }
`;

const MapFrame = styled.div`
  overflow: hidden;
  border: 1px solid rgba(33, 47, 57, 0.12);
  border-radius: 24px;
  background: #dfe8ea;
  box-shadow: 0 18px 48px rgba(24, 38, 46, 0.1);
`;

const MapContainer = styled.div`
  width: 100%;
  height: clamp(340px, 52vw, 600px);
`;
